import { useState, useEffect } from "react";
import { supabase } from '../supabase';
import { logActivity } from './ActivityLog';

const C = {
  bg:"#0e0e0e", sidebar:"#0a0a0a", surface:"#181818", surface2:"#202020",
  border:"#2a2a2a", muted:"#666", orange:"#FF5F1F", magenta:"#E8197D",
  gold:"#F5A623", green:"#4ade80", white:"#fff", red:"#f87171", blue:"#60a5fa",
};
const card = (x={}) => ({ background:C.surface, borderRadius:10, padding:16, border:`0.5px solid ${C.border}`, ...x });
const inp  = (x={}) => ({ background:C.surface2, border:`0.5px solid ${C.border}`, borderRadius:8, padding:"9px 12px", color:C.white, fontSize:13, outline:"none", width:"100%", boxSizing:"border-box", ...x });
const btn  = (v="primary",x={}) => ({ padding:"8px 16px", borderRadius:8, fontSize:13, fontWeight:500, cursor:"pointer", border:"none", boxSizing:"border-box", ...(v==="primary"?{background:`linear-gradient(135deg,${C.orange},${C.magenta})`,color:C.white}:{}), ...(v==="ghost"?{background:"none",border:`0.5px solid ${C.border}`,color:C.muted}:{}), ...(v==="danger"?{background:"#3d1515",color:C.red,border:`0.5px solid #f8717133`}:{}), ...x });
const lbl  = t => <div style={{fontSize:11,color:C.muted,marginBottom:5,textTransform:"uppercase",letterSpacing:0.5}}>{t}</div>;
const secTitle = t => <div style={{fontSize:11,color:C.muted,marginBottom:12,letterSpacing:0.5,textTransform:"uppercase"}}>{t}</div>;
const gradBar = <div style={{height:2,background:`linear-gradient(90deg,${C.orange},${C.magenta},${C.gold})`,borderRadius:1,marginBottom:24}}/>;
const BUCKET = "portals";
const rid = () => Math.random().toString(36).slice(2,8);
export const DEFAULT_TIERS = [{pct:5,threshold:100000},{pct:7,threshold:300000},{pct:9,threshold:600000},{pct:15,threshold:1000000}];

function Toast({ msg, ok=true }) {
  if(!msg) return null;
  return <div style={{position:"fixed",bottom:72,left:"50%",transform:"translateX(-50%)",background:C.surface,border:`0.5px solid ${ok?C.green:C.red}44`,color:ok?C.green:C.red,padding:"10px 20px",borderRadius:20,fontSize:13,zIndex:400,whiteSpace:"nowrap"}}>{msg}</div>;
}

function Logo({ url, name, size=40 }) {
  const initials = (name||"?").split(" ").map(w=>w[0]).slice(0,2).join("").toUpperCase();
  if(url) return <img src={url} alt={name} style={{width:size,height:size,borderRadius:8,objectFit:"cover",background:"#fff",flexShrink:0}}/>;
  return <div style={{width:size,height:size,borderRadius:8,background:`${C.orange}22`,border:`1px solid ${C.orange}55`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,color:C.orange,fontWeight:600,flexShrink:0}}>{initials}</div>;
}

// ---------- Gjenbrukbar kundevelger ----------
export function CustomerSelect({ customers, value, onChange, onCreate, required }) {
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const create = async () => {
    const name = newName.trim(); if(!name || busy) return;
    setBusy(true);
    const c = await onCreate(name);
    setBusy(false);
    if(c){ onChange(c.id); setCreating(false); setNewName(""); }
  };
  return (
    <div>
      <div style={{display:"flex",gap:8}}>
        <select style={{...inp(),appearance:"none"}} value={value||""} onChange={e=>onChange(e.target.value||null)}>
          <option value="">{required ? "— Velg kunde —" : "— Ingen —"}</option>
          {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <button type="button" style={{...btn("ghost"),whiteSpace:"nowrap",padding:"9px 12px"}} onClick={()=>setCreating(v=>!v)}>+ Ny</button>
      </div>
      {creating && (
        <div style={{display:"flex",gap:8,marginTop:8}}>
          <input style={inp()} placeholder="Kundenavn" value={newName} autoFocus
            onChange={e=>setNewName(e.target.value)} onKeyDown={e=>{ if(e.key==="Enter"){ e.preventDefault(); create(); } }}/>
          <button type="button" style={{...btn("primary"),whiteSpace:"nowrap"}} onClick={create}>{busy?"…":"Opprett"}</button>
        </div>
      )}
    </div>
  );
}

// ---------- Opprett/rediger-modal ----------
function CustomerModal({ existing, user, onClose, onSaved, showToast, isMobile }) {
  const [d, setD] = useState({
    name:"", org_no:"", contact_name:"", contact_email:"", contact_phone:"", address:"", billing_info:"", notes:"", logo_url:"",
    guide_price:17500, partner_discount_pct:0, volume_discount:false, volume_tiers:[], language:"no",
    ...(existing||{}),
  });
  const set = (k,v) => setD(x=>({ ...x, [k]:v }));
  const tiers = d.volume_tiers || [];
  const setTier = (i,k,v) => set("volume_tiers", tiers.map((t,j)=> j===i ? {...t,[k]:v===""?"":Number(v)} : t));
  const addTier = () => set("volume_tiers", [...tiers, {pct:"", threshold:""}]);
  const rmTier = i => set("volume_tiers", tiers.filter((_,j)=>j!==i));
  const enableVolume = on => { set("volume_discount", on); if(on && tiers.length===0) set("volume_tiers", DEFAULT_TIERS); };
  const [logoFile, setLogoFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if(!d.name.trim()){ showToast("Navn er påkrevd.", false); return; }
    setSaving(true);
    try {
      let id = existing?.id;
      const cleanTiers = (d.volume_tiers||[])
        .map(t=>({ pct:Number(t.pct)||0, threshold:Number(t.threshold)||0 }))
        .filter(t=>t.pct>0)
        .sort((a,b)=>a.pct-b.pct);
      const base = {
        name:d.name.trim(), org_no:d.org_no||null, contact_name:d.contact_name||null,
        contact_email:d.contact_email||null, contact_phone:d.contact_phone||null,
        address:d.address||null, billing_info:d.billing_info||null, notes:d.notes||null,
        guide_price:Number(d.guide_price)||0, partner_discount_pct:Number(d.partner_discount_pct)||0,
        volume_discount:!!d.volume_discount, volume_tiers:d.volume_discount?cleanTiers:[],
        language: d.language==="en" ? "en" : "no",
      };
      if(existing){
        const { error } = await supabase.from('customers').update({ ...base, updated_at:new Date().toISOString() }).eq('id', id);
        if(error) throw error;
      } else {
        const { data, error } = await supabase.from('customers').insert({ ...base, created_by:user?.id||null }).select().single();
        if(error) throw error;
        id = data.id;
      }
      // Logo (unik filnavn → unngå overskriv/RLS-konflikt)
      if(logoFile){
        const ext = (logoFile.name.split('.').pop()||'png').toLowerCase().replace(/[^a-z0-9]/g,'');
        const path = `customers/${id}/logo-${rid()}.${ext}`;
        const { error:upErr } = await supabase.storage.from(BUCKET).upload(path, logoFile, { contentType:logoFile.type||undefined });
        if(upErr) throw upErr;
        const url = supabase.storage.from(BUCKET).getPublicUrl(path).data.publicUrl;
        await supabase.from('customers').update({ logo_url:url }).eq('id', id);
      }
      // Navn endret → propager til koblede prosjekter/portaler (cachet visningsnavn)
      if(existing && existing.name !== d.name.trim()){
        await supabase.from('projects').update({ client:d.name.trim() }).eq('customer_id', id);
        await supabase.from('portals').update({ customer:d.name.trim() }).eq('customer_id', id);
      }
      logActivity(user, existing?'customer_updated':'customer_created', { name:d.name.trim() }, 'customers');
      showToast(existing ? "Kunde oppdatert." : "Kunde opprettet.");
      onSaved();
    } catch(e){
      showToast("Kunne ikke lagre: " + (e.message||e), false);
    } finally { setSaving(false); }
  };

  const modalStyle = isMobile?{position:"fixed",inset:0,background:C.surface,overflowY:"auto",padding:20,zIndex:100}:{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:100};
  const innerStyle = isMobile?{}:{background:C.surface,borderRadius:12,padding:28,width:540,maxHeight:"88vh",overflowY:"auto",border:`0.5px solid ${C.border}`};
  const logoPreview = logoFile ? URL.createObjectURL(logoFile) : d.logo_url;
  return (
    <div style={modalStyle} onClick={isMobile?undefined:onClose}>
      <div style={innerStyle} onClick={e=>e.stopPropagation()}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <div style={{fontSize:18,fontWeight:500}}>{existing?"Rediger kunde":"Ny kunde"}</div>
          <button onClick={onClose} style={{background:"none",border:"none",color:C.muted,fontSize:22,cursor:"pointer"}}>×</button>
        </div>
        <div style={{display:"flex",gap:14,alignItems:"center",marginBottom:16}}>
          <Logo url={logoPreview} name={d.name} size={56}/>
          <label style={{...btn("ghost"),padding:"7px 12px",fontSize:12,cursor:"pointer"}}>
            {logoFile?"Bytt logo":"Last opp logo"}
            <input type="file" accept="image/*" style={{display:"none"}} onChange={e=>setLogoFile(e.target.files?.[0]||null)}/>
          </label>
        </div>
        <div style={{marginBottom:14}}>{lbl("Navn *")}<input style={inp()} value={d.name} onChange={e=>set("name",e.target.value)} placeholder="Kundenavn"/></div>
        <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:12,marginBottom:14}}>
          <div>{lbl("Org.nr")}<input style={inp()} value={d.org_no||""} onChange={e=>set("org_no",e.target.value)}/></div>
          <div>{lbl("Kontaktperson")}<input style={inp()} value={d.contact_name||""} onChange={e=>set("contact_name",e.target.value)}/></div>
          <div>{lbl("E-post")}<input style={inp()} value={d.contact_email||""} onChange={e=>set("contact_email",e.target.value)}/></div>
          <div>{lbl("Telefon")}<input style={inp()} value={d.contact_phone||""} onChange={e=>set("contact_phone",e.target.value)}/></div>
        </div>
        <div style={{marginBottom:14}}>{lbl("Adresse")}<input style={inp()} value={d.address||""} onChange={e=>set("address",e.target.value)}/></div>
        <div style={{marginBottom:14}}>{lbl("Fakturainfo")}<textarea style={inp({minHeight:50,resize:"vertical"})} value={d.billing_info||""} onChange={e=>set("billing_info",e.target.value)} placeholder="Fakturaadresse, referanse, e-post for faktura…"/></div>
        <div style={{marginBottom:14}}>{lbl("Notater")}<textarea style={inp({minHeight:60,resize:"vertical"})} value={d.notes||""} onChange={e=>set("notes",e.target.value)}/></div>
        <div style={{marginBottom:18}}>{lbl("Standardspråk (for tilbud)")}
          <div style={{display:"flex",gap:8}}>
            {[{code:"no",label:"Norsk"},{code:"en",label:"English"}].map(l=>(
              <button key={l.code} type="button" onClick={()=>set("language",l.code)}
                style={{...btn((d.language||"no")===l.code?"primary":"ghost"),padding:"6px 16px",fontSize:12}}>{l.label}</button>
            ))}
          </div>
        </div>

        {/* Pris */}
        <div style={{borderTop:`0.5px solid ${C.border}`,paddingTop:16,marginBottom:18}}>
          {secTitle("Pris")}
          <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:12,marginBottom:14}}>
            <div>{lbl("Veiledende pris (NOK)")}<input style={inp()} type="number" value={d.guide_price} onChange={e=>set("guide_price",e.target.value)}/></div>
            <div>{lbl("Partner-rabatt (%)")}<input style={inp()} type="number" value={d.partner_discount_pct} onChange={e=>set("partner_discount_pct",e.target.value)}/>
              <div style={{fontSize:11,color:C.muted,marginTop:4}}>0 = ingen partner-rabatt (vis kun én pris)</div></div>
          </div>
          <div onClick={()=>enableVolume(!d.volume_discount)} style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",marginBottom:d.volume_discount?12:0}}>
            <span style={{width:18,height:18,borderRadius:5,border:`1.5px solid ${d.volume_discount?C.green:C.border}`,background:d.volume_discount?C.green:"transparent",color:"#0a0a0a",fontSize:12,display:"flex",alignItems:"center",justifyContent:"center"}}>{d.volume_discount?"✓":""}</span>
            <span style={{fontSize:14}}>Volumrabatt</span>
          </div>
          {d.volume_discount && (
            <div style={{paddingLeft:4}}>
              <div style={{display:"flex",gap:10,fontSize:11,color:C.muted,textTransform:"uppercase",letterSpacing:0.5,marginBottom:6}}>
                <span style={{width:90}}>Rabatt %</span><span>Fra (NOK)</span>
              </div>
              {tiers.map((t,i)=>(
                <div key={i} style={{display:"flex",gap:10,marginBottom:8,alignItems:"center"}}>
                  <input style={{...inp(),width:90}} type="number" placeholder="%" value={t.pct} onChange={e=>setTier(i,"pct",e.target.value)}/>
                  <input style={inp()} type="number" placeholder="terskel" value={t.threshold} onChange={e=>setTier(i,"threshold",e.target.value)}/>
                  <button type="button" style={{...btn("danger"),padding:"6px 10px",fontSize:12}} onClick={()=>rmTier(i)}>×</button>
                </div>
              ))}
              <button type="button" style={{...btn("ghost"),padding:"6px 12px",fontSize:12,marginTop:2}} onClick={addTier}>+ Legg til trinn</button>
            </div>
          )}
        </div>

        <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
          <button style={btn("ghost")} onClick={onClose}>Avbryt</button>
          <button style={btn("primary")} onClick={save} disabled={saving}>{saving?"Lagrer…":"Lagre"}</button>
        </div>
      </div>
    </div>
  );
}

// ---------- Hovedside ----------
export function CustomersPage({ customers, projects, user, onReload, onOpenProject, isMobile }) {
  const [modal, setModal] = useState(null);      // {} for ny, kunde-objekt for edit
  const [expanded, setExpanded] = useState(null);
  const [portals, setPortals] = useState([]);
  const [toast, setToast] = useState({ msg:"", ok:true });
  const showToast = (msg, ok=true) => { setToast({msg,ok}); setTimeout(()=>setToast({msg:"",ok:true}),2800); };

  useEffect(() => { supabase.from('portals').select('id,slug,customer_id').then(({data})=>setPortals(data||[])); }, []);

  const projOf = id => (projects||[]).filter(p => p.customer_id===id);
  const portsOf = id => (portals||[]).filter(p => p.customer_id===id);

  const del = async (c) => {
    const np = projOf(c.id).length, npo = portsOf(c.id).length;
    const warn = (np||npo) ? `\n\n${np} prosjekt og ${npo} portal mister koblingen (slettes ikke).` : "";
    if(!window.confirm(`Slette kunden «${c.name}»?${warn}`)) return;
    const { error } = await supabase.from('customers').delete().eq('id', c.id);
    if(error){ showToast("Kunne ikke slette.", false); return; }
    showToast("Kunde slettet.");
    onReload();
  };

  const sorted = [...(customers||[])].sort((a,b)=>a.name.localeCompare(b.name,"nb"));

  return (
    <div style={{paddingBottom:isMobile?80:0}}>
      <Toast msg={toast.msg} ok={toast.ok}/>
      {gradBar}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8,gap:8,flexWrap:"wrap"}}>
        <div style={{fontSize:20,fontWeight:500}}>Kunder</div>
        <button style={{...btn("primary"),padding:"7px 16px",fontSize:13}} onClick={()=>setModal({})}>+ Ny kunde</button>
      </div>
      <div style={{fontSize:13,color:C.muted,marginBottom:24,maxWidth:620}}>
        Sentralt kunderegister. Prosjekter og kundeportaler knyttes til en kunde.
      </div>

      <div style={{maxWidth:640}}>
        {sorted.length===0 && <div style={{color:C.muted,fontSize:13,padding:"8px 0"}}>Ingen kunder ennå.</div>}
        {sorted.map(c => {
          const ps = projOf(c.id), pos = portsOf(c.id);
          const open = expanded===c.id;
          return (
            <div key={c.id} style={{...card(),marginBottom:10}}>
              <div style={{display:"flex",alignItems:"center",gap:12}}>
                <Logo url={c.logo_url} name={c.name}/>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:15,fontWeight:500}}>{c.name}</div>
                  <div style={{fontSize:12,color:C.muted}}>
                    {c.contact_name ? c.contact_name+" · " : ""}{ps.length} prosjekt · {pos.length} portal
                  </div>
                </div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  <button style={{...btn("ghost"),padding:"6px 12px",fontSize:12}} onClick={()=>setExpanded(open?null:c.id)}>{open?"Skjul":"Vis"}</button>
                  <button style={{...btn("ghost"),padding:"6px 12px",fontSize:12}} onClick={()=>setModal(c)}>Rediger</button>
                  <button style={{...btn("danger"),padding:"6px 12px",fontSize:12}} onClick={()=>del(c)}>Slett</button>
                </div>
              </div>
              {open && (
                <div style={{marginTop:14,paddingTop:14,borderTop:`0.5px solid ${C.border}`,fontSize:13}}>
                  {(c.contact_email||c.contact_phone||c.org_no) && (
                    <div style={{color:C.muted,marginBottom:10,display:"flex",gap:16,flexWrap:"wrap"}}>
                      {c.org_no && <span>Org: {c.org_no}</span>}
                      {c.contact_email && <span>{c.contact_email}</span>}
                      {c.contact_phone && <span>{c.contact_phone}</span>}
                    </div>
                  )}
                  <div style={{marginBottom:6,color:C.muted,fontSize:11,textTransform:"uppercase",letterSpacing:0.5}}>Prosjekter</div>
                  {ps.length===0 && <div style={{color:C.muted,marginBottom:10}}>Ingen.</div>}
                  {ps.map(p => (
                    <div key={p.id} style={{display:"flex",justifyContent:"space-between",padding:"4px 0"}}>
                      <span style={{cursor:onOpenProject?"pointer":"default",color:onOpenProject?C.blue:C.white}} onClick={()=>onOpenProject&&onOpenProject(p)}>{p.name}</span>
                      <span style={{color:C.muted,fontSize:12}}>{p.date}</span>
                    </div>
                  ))}
                  <div style={{margin:"10px 0 6px",color:C.muted,fontSize:11,textTransform:"uppercase",letterSpacing:0.5}}>Portaler</div>
                  {pos.length===0 && <div style={{color:C.muted}}>Ingen.</div>}
                  {pos.map(p => (
                    <div key={p.id} style={{display:"flex",justifyContent:"space-between",padding:"4px 0",alignItems:"center"}}>
                      <a href={`https://app.royal.show/p/${p.slug}`} target="_blank" rel="noreferrer" style={{color:C.blue,textDecoration:"none"}}>/p/{p.slug}</a>
                      <button style={{...btn("ghost"),padding:"3px 9px",fontSize:11}} onClick={()=>{ navigator.clipboard.writeText(`https://app.royal.show/p/${p.slug}`); showToast("Lenke kopiert!"); }}>Kopier</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {modal && <CustomerModal existing={modal.id?modal:null} user={user} isMobile={isMobile}
        onClose={()=>setModal(null)} showToast={showToast}
        onSaved={()=>{ setModal(null); onReload(); }}/>}
    </div>
  );
}
